import { useMutation, useQuery } from '@apollo/client'
import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Popover,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { type GetProcessStatusOptionsQuery } from '__generated__/graphql'
import { type FC, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { UpdateProcessStatusAcrossRosters } from 'recruiter/graphql/updateProcessStatus.gql'
import { useRosterSqlSearch } from 'recruiter/hooks'
import { AddCandidateToMultipleRosters } from 'recruiter/pages/Rosters/RosterProfile/CompareTable/Compare.gql'
import useToast from 'shared/components/Toast/useToast'
import { sortProcessStatusesBySummaryGroup } from 'shared/utils'
import { type RosterTableRowModel } from '../peopleRosterColumns'

interface BulkAddToRostersProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    anchorEl: HTMLButtonElement | null
    candidateId: string
    selectedRosterIds: string[]
}

type ProcessStatusOption = NonNullable<
    GetProcessStatusOptionsQuery['processStatuses'][number]
>

const BulkAddToRosters: FC<BulkAddToRostersProps> = ({
    isOpen,
    onClose,
    onSuccess,
    anchorEl,
    candidateId,
    selectedRosterIds,
}) => {
    const toast = useToast()
    const location = useLocation()
    const { data: tableData, refetch } = useRosterSqlSearch()

    const [selectedProcessStatus, setSelectedProcessStatus] =
        useState<ProcessStatusOption | null>(null)
    const [internalNote, setInternalNote] = useState('')
    const [externalNote, setExternalNote] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const rows = tableData as RosterTableRowModel[]
    const hasNotOn = selectedRosterIds.some(
        (id) => !rows.find((r) => r.id === id)?.rosterCandidateId
    )
    const hasAlreadyOn = selectedRosterIds.some(
        (id) => !!rows.find((r) => r.id === id)?.rosterCandidateId
    )
    const actionLabel =
        hasNotOn && hasAlreadyOn
            ? 'Apply to'
            : hasAlreadyOn
              ? 'Update'
              : 'Add to'
    const n = selectedRosterIds.length
    const searchPlural = n !== 1 ? 'es' : ''
    const isAllTable = location.pathname.endsWith('/all')
    const alreadyOnCount = selectedRosterIds.filter(
        (id) => !!rows.find((r) => r.id === id)?.rosterCandidateId
    ).length
    const showOverwriteWarning = isAllTable && hasAlreadyOn

    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    const [addCandidateToMultipleRosters] = useMutation(
        AddCandidateToMultipleRosters
    )

    const [updateProcessStatusAcrossRosters] = useMutation(
        UpdateProcessStatusAcrossRosters
    )

    const handleCancel = () => {
        setSelectedProcessStatus(null)
        setInternalNote('')
        setExternalNote('')
        onClose()
    }

    const handleButtonClick = () => {
        if (!selectedProcessStatus) {
            toast({
                title: 'Error',
                message: 'Please select a process status',
                severity: 'error',
            })
            return
        }
        if (showOverwriteWarning) {
            setConfirmOpen(true)
            return
        }
        handleSave()
    }

    const handleSave = async () => {
        if (!selectedProcessStatus) return
        setConfirmOpen(false)

        const rows = tableData as RosterTableRowModel[]
        const notOnRosterIds: string[] = []
        const alreadyOnRosterCandidateIds: string[] = []

        for (const rosterId of selectedRosterIds) {
            const row = rows.find((r) => r.id === rosterId)
            if (row?.rosterCandidateId) {
                alreadyOnRosterCandidateIds.push(row.rosterCandidateId)
            } else {
                notOnRosterIds.push(rosterId)
            }
        }

        const promises: Promise<unknown>[] = []

        if (notOnRosterIds.length > 0) {
            promises.push(
                addCandidateToMultipleRosters({
                    variables: {
                        candidateId,
                        rosterIds: notOnRosterIds,
                        processStatusId: selectedProcessStatus.id,
                        internalNote: internalNote || undefined,
                        externalNote: externalNote || undefined,
                    },
                })
            )
        }

        if (alreadyOnRosterCandidateIds.length > 0) {
            promises.push(
                updateProcessStatusAcrossRosters({
                    variables: {
                        rosterCandidateIds: alreadyOnRosterCandidateIds,
                        processStatus: selectedProcessStatus.status,
                        internalNote: internalNote || undefined,
                        externalNote: externalNote || undefined,
                    },
                })
            )
        }

        setIsLoading(true)
        try {
            await Promise.all(promises)
            await refetch()
            toast({
                title: 'Success',
                message: `${hasNotOn && hasAlreadyOn ? 'Applied to' : hasAlreadyOn ? 'Updated' : 'Added to'} ${n} search${searchPlural}`,
                severity: 'success',
            })
            setSelectedProcessStatus(null)
            setInternalNote('')
            setExternalNote('')
            onSuccess()
        } catch (error: any) {
            toast({
                title: 'Error',
                message: error.message || 'Error applying to searches',
                severity: 'error',
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            p: 2,
                            width: 320,
                            mt: 0.5,
                            boxShadow: 2,
                        },
                    },
                }}
            >
                <Stack spacing={1.5}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                        {actionLabel} {n} Search{searchPlural}
                    </Typography>

                    <Autocomplete
                        disabled={isLoading}
                        value={selectedProcessStatus}
                        onChange={(_, value) => setSelectedProcessStatus(value)}
                        options={sortProcessStatusesBySummaryGroup(
                            processStatusOptions?.processStatuses ?? []
                        )}
                        getOptionLabel={(option) => option.statusLabel}
                        isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                        }
                        groupBy={(option) =>
                            option.processStatusSummaryGroup?.label || ''
                        }
                        size="small"
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                key={option.id}
                                style={{
                                    fontSize: '0.75rem',
                                    padding: '4px 12px',
                                }}
                            >
                                {option.statusLabel}
                            </li>
                        )}
                        slotProps={{
                            paper: {
                                sx: {
                                    '& .MuiAutocomplete-groupLabel': {
                                        fontSize: '0.6875rem',
                                        lineHeight: 2,
                                        fontWeight: 600,
                                    },
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Process status..."
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '0.75rem',
                                    },
                                }}
                            />
                        )}
                    />

                    <TextField
                        variant="outlined"
                        placeholder="Internal note (CPI only)..."
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        disabled={isLoading}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '0.75rem',
                            },
                        }}
                    />

                    <TextField
                        variant="outlined"
                        placeholder="Client-visible note..."
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                        value={externalNote}
                        onChange={(e) => setExternalNote(e.target.value)}
                        disabled={isLoading}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '0.75rem',
                            },
                        }}
                    />

                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                    >
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleCancel}
                            disabled={isLoading}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleButtonClick}
                            disabled={!selectedProcessStatus || isLoading}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                `${actionLabel} ${n} Search${searchPlural}`
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle
                    sx={{ fontSize: '0.9375rem', fontWeight: 600, pb: 0.5 }}
                >
                    Overwrite existing status?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: '0.8125rem' }}>
                        {alreadyOnCount} of the selected search
                        {alreadyOnCount !== 1 ? 'es' : ''} already
                        {alreadyOnCount === 1 ? ' has' : ' have'} a process
                        status assigned. This will overwrite{' '}
                        {alreadyOnCount === 1 ? 'it' : 'them'}.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        size="small"
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        size="small"
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default BulkAddToRosters
